import React, { useMemo } from 'react';
import { FEED_CHIP } from './investigation/feedCardStyles';
import InspectDrawerShell, { OpenLinkButton } from './InspectDrawerShell';

function normalizeRecords(data) {
  if (Array.isArray(data?.legal_records)) return data.legal_records;
  if (Array.isArray(data?.details?.records)) return data.details.records;
  return [];
}

/**
 * Court & Registry inspect — right slide-over drawer.
 *
 * @param {{ isOpen: boolean, onClose: Function, data?: object }} props
 */
export default function LegalCourtsInspectModal({ isOpen, onClose, data }) {
  const records = useMemo(() => normalizeRecords(data), [data]);
  const count = records.length;
  const clean = count === 0;
  const companyName = String(data?.company_name || data?.details?.company_name || '').trim();
  const companyDomain = String(data?.domain || data?.details?.domain || '').trim();
  const primarySourceUrl = String(records[0]?.url || data?.url || '').trim();

  if (!isOpen) return null;

  return (
    <InspectDrawerShell
      isOpen={isOpen}
      onClose={onClose}
      ariaLabel="Court and registry intelligence inspection"
      testId="legal-courts-inspect-modal"
      eyebrow="Court & Registry Intelligence"
      title="Court & Registry Signals"
      sourceHref={primarySourceUrl}
      subtitle={
        <>
          {companyName ? <p className="mt-1 text-sm text-slate-300">{companyName}</p> : null}
          {companyDomain ? (
            <p className="mt-0.5 font-mono text-[11px] text-slate-500">{companyDomain}</p>
          ) : null}
        </>
      }
    >
      <div
        className={`rounded-xl border p-3 text-sm ${
          clean
            ? 'border-emerald-500/30 bg-emerald-950/30 text-emerald-100'
            : 'border-indigo-500/30 bg-indigo-950/30 text-indigo-100'
        }`}
        data-testid="legal-courts-inspect-summary"
      >
        {clean
          ? 'No attested court, registry, or public-record signals were found for this entity.'
          : `${count} court, registry, or public-record signal(s) matched this corporate target.`}
      </div>

      {records.length > 0 ? (
        <ul className="m-0 list-none space-y-3 p-0" data-testid="legal-courts-inspect-list">
          {records.map((record, idx) => {
            const title = String(record.title || 'Legal record').trim();
            const url = String(record.url || '').trim();
            const platform = String(record.platform || 'Legal Portal').trim();
            const jurisdiction = String(record.details?.jurisdiction || '').trim();
            const snippet = String(record.snippet || '').trim();
            const filed =
              String(record.details?.date || record.date || record.published_at || '').trim() ||
              null;
            const entities = [
              companyName,
              ...(Array.isArray(record.details?.entities) ? record.details.entities : []),
            ]
              .map((e) => String(e || '').trim())
              .filter(Boolean);

            return (
              <li
                key={`${url}-${title}-${idx}`}
                className="rounded-xl border border-white/10 bg-slate-950/60 p-4"
                data-testid="legal-courts-inspect-item"
              >
                <div className="flex flex-wrap gap-1.5">
                  <span className={`${FEED_CHIP} border-indigo-500/30 text-indigo-200`}>
                    {platform}
                  </span>
                  {jurisdiction ? (
                    <span className={`${FEED_CHIP} uppercase`}>{jurisdiction}</span>
                  ) : null}
                  {record.confidence ? (
                    <span className={FEED_CHIP}>{record.confidence} Match</span>
                  ) : null}
                </div>
                <h4 className="mt-2 text-sm font-bold text-slate-100">{title}</h4>
                {filed ? <p className="mt-1 text-[11px] text-slate-500">{filed}</p> : null}
                {snippet ? (
                  <p className="mt-1 text-xs leading-relaxed text-slate-400">{snippet}</p>
                ) : null}
                {entities.length > 0 ? (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {entities.slice(0, 6).map((ent) => (
                      <span key={ent} className={FEED_CHIP}>
                        {ent}
                      </span>
                    ))}
                  </div>
                ) : null}
                {url ? (
                  <div className="mt-3">
                    <OpenLinkButton href={url} />
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
      ) : null}
    </InspectDrawerShell>
  );
}
